<template>
  <button
    class="LmButtonNext group relative flex items-center h-8 font-mono"
    :class="[ccButton, !icon ? 'px-4' : iconLeft ? 'pl-3 pr-4' : 'pl-4 pr-3']"
    @click.prevent="onClick"
  >
    <lm-loading
      v-if="loading"
      class="absolute inset-x-0 mx-auto"
      :color="loadingColor"
      :size="21"
    />
    <span :class="iconLeft ? 'order-1' : ''">{{ label }}</span>
    <lm-icon
      v-if="icon"
      :icon="icon"
      size="18"
      :class="[ccIcon, iconLeft ? 'mr-2 order-0' : 'ml-2']"
    />
  </button>
</template>

<script>
import LmIcon from '@/components/ui/LmIcon'
import LmLoading from '@/components/ui/LmLoading'
import {ButtonType} from '@/lib/definitions'

export default {
  name: 'LmButtonNext',
  components: {LmIcon, LmLoading},
  props: {
    label: {
      type: String,
      required: true
    },
    type: {
      validator(v) {
        const values = [
          ButtonType.DEFAULT,
          ButtonType.PRIMARY,
          ButtonType.SECONDARY,
          ButtonType.FLAT,
          ButtonType.OUTLINE,
          ButtonType.WARNING,
          ButtonType.ACCENT
        ]
        return values.indexOf(v) >= 0 ? v : false
      },
      default: ButtonType.DEFAULT
    },
    disabled: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    icon: {
      type: String,
      default: null
    },
    iconLeft: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ccIcon() {
      if (this.loading) return 'text-transparent'
      let color = this.disabled ? 'text-gray-300' : 'text-gray-700'
      if (this.type === ButtonType.PRIMARY)
        color = this.disabled
          ? 'text-gray-500'
          : 'text-white group-hover:text-gray-900'
      else if (this.type === ButtonType.SECONDARY)
        color = this.disabled
          ? 'text-gray-300'
          : 'text-white group-hover:text-highlight'
      else if (this.type === ButtonType.WARNING)
        color = this.disabled
          ? 'text-gray-300'
          : 'text-white group-hover:text-error'
      return color
    },
    ccButton() {
      let font =
        this.type === ButtonType.FLAT
          ? 'text-xs capitalize'
          : 'text-xxs uppercase'
      let style = ''
      let hover = ''
      if (this.disabled) {
        style = 'cursor-not-allowed'
        switch (this.type) {
          case ButtonType.DEFAULT:
            style += ' text-gray-500 bg-gray-200'
            break
          case ButtonType.PRIMARY:
            style += ' text-gray-500 bg-gray-900'
            break
          case ButtonType.SECONDARY:
            style += ' text-gray-500 bg-gray-200'
            break
          case ButtonType.OUTLINE:
            style += ' text-gray-300 outline-gray-300'
            break
          case ButtonType.WARNING:
            style += ' text-gray-300 bg-error'
            break
          case ButtonType.FLAT:
            style += ' text-gray-500'
            break
        }
      } else {
        hover =
          'hover:text-gray-900 hover:outline-gray-900 hover:bg-transparent'
        switch (this.type) {
          case ButtonType.DEFAULT:
            style = this.loading ? 'text-transparent' : 'text-gray-900'
            style += ' bg-gray-300'
            break
          case ButtonType.PRIMARY:
            style = this.loading ? 'text-transparent' : 'text-white'
            style += ' bg-gray-900'
            hover =
              'hover:text-gray-900 hover:outline-gray-900 hover:bg-transparent'
            break
          case ButtonType.SECONDARY:
            style = this.loading ? 'text-transparent' : 'text-white'
            style += ' bg-highlight'
            hover =
              'hover:text-gray-900 hover:outline-gray-900 hover:bg-transparent'
            break
          case ButtonType.OUTLINE:
            style = this.loading ? 'text-transparent' : 'text-gray-700'
            style += ' bg-transparent outline-gray-300'
            break
          case ButtonType.WARNING:
            style = this.loading ? 'text-transparent' : 'text-white'
            style += ' bg-error'
            hover = 'hover:text-error hover:outline-error hover:bg-transparent'
            break
          case ButtonType.ACCENT:
            style = this.loading ? 'text-transparent' : 'text-white'
            style += ' bg-accent'
            hover =
              'hover:text-accent hover:outline-accent hover:bg-transparent'
            break
          case ButtonType.FLAT:
            style = this.loading ? 'text-transparent' : 'text-gray-700'
            break
        }
      }
      if (this.loading) hover = 'cursor-default'
      return `${font} ${style} ${hover}`
    },
    loadingColor() {
      if (
        this.type === ButtonType.PRIMARY ||
        this.type === ButtonType.SECONDARY ||
        this.type === ButtonType.WARNING
      )
        return '#ffffff'
      return '#a7a7a7'
    }
  },
  methods: {
    onClick(e) {
      if (!this.disabled && !this.loading) this.$emit('click', e)
    }
  }
}
</script>

<style>
.js-focus-visible .LmButtonNext:focus:not(.focus-visible) {
  outline: 1px solid currentColor;
}
</style>
